import React, { useState, useEffect } from "react";
import { FormattedMessage } from 'react-intl'
import * as Utils from '../../commons/utils/Utils.js'
import {
	Table,
	TableContainer,
	TableHead,
	TableRow,
	TableBody,
	TablePagination,
	CircularProgress,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import UpIcon from '@material-ui/icons/KeyboardArrowUp'
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles.js'
import CentroNotificacionesGridRow from './CentroNotificacionesGridRow.jsx'
import CentroNotificacionesGridFiltros from './CentroNotificacionesGridFiltros.jsx'
import Sort from "../../assets/images/llantas/table/sort.svg";
import SortAsc from "../../assets/images/llantas/table/sort-asc.svg";
import SortDesc from "../../assets/images/llantas/table/sort-desc.svg";

const PAGINATION_INIT = {
	page: 0,
	perPage: 10,
}

const FILTER_QUERY_INIT = {
	estado: 'todas',
	texto: '',
	fechaDesde: '',
	fechaHasta: '',
}

const SORT_QUERY_INIT = {
	fechaHora: 'desc',
	titulo: '',
}

// Key propia de localStorage (VEC-3444 §5.3): si "Persistir Filtros" está activo,
// filterQuery se guarda acá y se restaura al montar; si no, la key ni se escribe.
const STORAGE_KEY = 'centroNotificaciones.filtros'

const leerFiltrosPersistidos = () => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		return raw ? JSON.parse(raw) : null
	} catch (e) {
		return null
	}
}

const CentroNotificacionesGrid = () => {
	const [loading, setLoading] = useState(false)
	const [dropdownActive, setDropdownActive] = useState(false)

	const [pagination, setPagination] = useState({ ...PAGINATION_INIT })
	const [paginationCount, setPaginationCount] = useState(0)

	const [filterQuery, setFilterQuery] = useState(() => leerFiltrosPersistidos() || { ...FILTER_QUERY_INIT })
	const [persistirFiltros, setPersistirFiltros] = useState(() => leerFiltrosPersistidos() !== null)
	const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT })

	const [notificaciones, setNotificaciones] = useState([])

	useEffect(() => {
		if (persistirFiltros) {
			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filterQuery));
		} else {
			window.localStorage.removeItem(STORAGE_KEY);
		}
	}, [persistirFiltros, filterQuery])

	useEffect(() => {
		fetchHistorial();
		return () => { }
	}, [pagination, filterQuery, sortQuery])

	const getQuery = () => {
		let q = "?page=" + (pagination.page + 1) + "&perPage=" + pagination.perPage
		q += '&estado=' + filterQuery.estado
		q += '&texto=' + filterQuery.texto
		q += '&fechaDesde=' + filterQuery.fechaDesde
		q += '&fechaHasta=' + filterQuery.fechaHasta
		q += '&fechaHoraSort=' + sortQuery.fechaHora
		q += '&tituloSort=' + sortQuery.titulo
		return q
	}

	const fetchHistorial = () => {
		setLoading(true)
		Promise.all([
			Utils.getData('/notificaciones/historial' + getQuery()),
		])
			.then((data) => {
				setNotificaciones(data[0].notificaciones)
				setPaginationCount(data[0].pagination.count)
				setLoading(false)
			})
			.catch(() => {
				setLoading(false)
			})
	}

	const handleChangePage = (event, newPage) => {
		setPagination({ ...pagination, page: newPage })
	}

	const handleChangeRowsPerPage = (event) => {
		setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 })
	}

	const handleFilterChange = () => {
		setPagination(prevState => ({ ...prevState, page: 0 }))
	}

	const requestSort = (key) => {
		const otroKey = key === 'fechaHora' ? 'titulo' : 'fechaHora'
		setSortQuery(prevState => {
			const actual = prevState[key]
			const siguiente = !actual ? 'asc' : (actual === 'desc' ? 'asc' : 'desc')
			return { [otroKey]: '', [key]: siguiente }
		})
		setPagination(prevState => ({ ...prevState, page: 0 }))
	}

	// Decisión cerrada (doc §3.2): el click en la fila SIEMPRE marca como leída;
	// si además tiene url, navega recién después de que el PATCH confirme.
	const marcarComoLeida = (notificacion) => {
		Utils.sendData('/notificaciones/' + notificacion.id + '/leer', 'PATCH')
			.then(() => {
				setNotificaciones(prevState =>
					prevState.map(n => (n.id === notificacion.id ? { ...n, leida: true } : n))
				)
				window.dispatchEvent(new Event('nueva-notificacion'))
				if (notificacion.url) {
					window.location.href = notificacion.url
				}
			})
			.catch(() => { })
	}

	return (
		<div className="card">
			<div className="card-content collpase show">
				<div className="card-body card-dashboard">
					<div className="container-fluid mb-1">
						<div className="row dt-icons">
							<div className="col">
								<div
									className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
									onClick={() => setDropdownActive(!dropdownActive)}
								>
									{dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
								</div>
							</div>
						</div>
					</div>

					<CentroNotificacionesGridFiltros
						dropdownActive={dropdownActive}
						filterQuery={filterQuery}
						setFilterQuery={setFilterQuery}
						persistirFiltros={persistirFiltros}
						setPersistirFiltros={setPersistirFiltros}
						handleFilterChange={handleFilterChange}
					/>

					<TableContainer>
						<Table aria-label="centro notificaciones table" className="table-hover">
							<TableHead>
								<TableRow>
									<StyledTableCell align="center" style={{ width: "60px" }}>
										<FormattedMessage id="centroNotificaciones.grid.columna_estado" defaultMessage="Estado" />
									</StyledTableCell>
									<StyledTableCell align="left" onClick={() => requestSort("titulo")} style={{ cursor: "pointer" }}>
										<FormattedMessage id="centroNotificaciones.grid.columna_titulo" defaultMessage="Título" />
										<img className="ml-1" src={sortQuery.titulo === "" ? Sort : sortQuery.titulo === "asc" ? SortAsc : SortDesc} alt="/" style={{ height: "12px", width: "22px" }} />
									</StyledTableCell>
									<StyledTableCell align="left">
										<FormattedMessage id="centroNotificaciones.grid.columna_mensaje" defaultMessage="Mensaje" />
									</StyledTableCell>
									<StyledTableCell align="center" style={{ width: "60px" }}>
										<FormattedMessage id="centroNotificaciones.grid.columna_link" defaultMessage="Link" />
									</StyledTableCell>
									<StyledTableCell align="left" onClick={() => requestSort("fechaHora")} style={{ cursor: "pointer", width: "180px" }}>
										<FormattedMessage id="centroNotificaciones.grid.columna_fecha" defaultMessage="Fecha y hora" />
										<img className="ml-1" src={sortQuery.fechaHora === "" ? Sort : sortQuery.fechaHora === "asc" ? SortAsc : SortDesc} alt="/" style={{ height: "12px", width: "22px" }} />
									</StyledTableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading && (
									<TableRow>
										<StyledTableCell colSpan={5} align="center">
											<CircularProgress />
										</StyledTableCell>
									</TableRow>
								)}
								{!loading && (
									notificaciones.length > 0 ?
										notificaciones.map((notificacion) => (
											<CentroNotificacionesGridRow
												key={'centroNotificacionesGridRow_' + notificacion.id}
												notificacion={notificacion}
												onRowClick={marcarComoLeida}
											/>
										))
										:
										<TableRow>
											<StyledTableCell colSpan={5} align="center">
												<FormattedMessage id="centroNotificaciones.grid.sin_notificaciones" defaultMessage="No tenés notificaciones." />
											</StyledTableCell>
										</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25, 50, 100]}
						component="div"
						count={paginationCount}
						rowsPerPage={pagination.perPage}
						page={pagination.page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						labelRowsPerPage="Mostrar"
						labelDisplayedRows={({ from, to, count }) => "Mostrando " + from + " a " + to + " de " + count}
					/>
				</div>
			</div>
		</div>
	)
}

export default CentroNotificacionesGrid
